import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Form, Input, Checkbox, Spin, Radio, Select } from 'antd';
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import OTPModal from "../../../components/modals/OTPModal.jsx";
import { VerifyWithdrawTransactionApi, ResendWithdrawTransactionApi } from '../../../api/request/wallet/withdraw.js';
import Balance from '../../../assets/Balance.jsx';
import Account from '../../../assets/common/Account';
import Dollar from '../../../assets/common/Dollar';
import { GetMT5AccountsApi, GetWalletApi, InternalTransferApi } from '../../../api/request/transfer.js';
import TransferRequest from './TransferRequest.jsx';
import { WalletOutlined, PlusCircleOutlined, DownOutlined, BankOutlined } from '@ant-design/icons';
import BalanceCard from '../dashboard/components/BalanceCardSM.jsx';
import { handleApiError } from '../../../utils/handleApiError.js';
import { GetTermsByTermsApi } from '../../../api/request/common.js';


const TransferMobile = () => {
  const { userDetails } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const [wallet, setWallet] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount, setToAccount] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [tncFileUrl, setTncFileUrl] = useState(null);
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);

  const getWallet = async () => {
    setIsLoading(true);
    try {
      const params = { RuleType: 'InternalTransfer' };
      const { data } = await GetWalletApi(userDetails?._id, new URLSearchParams(params).toString());
      if (data?.success) setWallet(data?.data)
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetMT5AccountsApi(true);
      if (data?.success) {
        setAccounts([
          { login: "wallet" },
          ...data?.data?.filter((account) => account?.isLive),
        ]);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getWallet();
    getAccounts();
  }, []);


  const resetAll = () => {
    form.resetFields();
    setFromAccount(null);
    setToAccount(null);
  }

  const handleSubmit = async (values) => {
    setIsSubmitLoading(true);
    try {
      const isWalletToAccount = values?.fromAccount === 'wallet';
      const isAccountToWallet = values?.toAccount === 'wallet';
      let data = null;

      if (isWalletToAccount || isAccountToWallet) {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: isAccountToWallet ? values?.fromAccount : 0,
          destinationTerminalId: isWalletToAccount ? values?.toAccount : 0,
          transferType: isWalletToAccount ? 1 : 2,
        });
        data = res?.data;
      } else {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: values?.fromAccount,
          destinationTerminalId: values?.toAccount,
          transferType: 3,
        });
        data = res?.data;
      }

      if (data?.success) {
        toast.success(data?.message);
        setRequestId(data?.data?.requestId);
        setOtp("");
        setIsOTPModalOpen(true);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  }
  const handleOtpSubmit = async (otpVal) => {
    try {
      setOtpLoading(true);
      const params = { requestId, otp: otpVal, actionType: 2 };
      const { data } = await VerifyWithdrawTransactionApi(params);
      if (data?.success) {
        toast.success(data?.message || 'Transfer verified successfully');
        setIsOTPModalOpen(false);
        setOtp("");
        resetAll();
        getWallet();
        getAccounts();
        setIsRefresh(true);
      } else {
        toast.error(data?.message || 'Failed to verify OTP');
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpResend = async () => {
    try {
      setOtp("");
      if (!requestId) {
        toast.error('Please initiate transfer first');
        return false;
      }
      const params = { requestId, actionType: 2 };
      const { data } = await ResendWithdrawTransactionApi(params);
      if (data?.success) {
        toast.success(data?.message || 'OTP resent');
        return true;
      }
      toast.error(data?.message || 'Failed to resend OTP');
      return false;
    } catch (err) {
      handleApiError(err);
      return false;
    }
  };

  const validateAmount = (_, value) => {
    if (!value) return Promise.reject(new Error('Please enter amount.'));

    if (Number(value) <= 0)
      return Promise.reject(new Error('Please enter a valid amount.'));

    if (wallet?.isRuleActive) {
      if (value < wallet?.minAmount) {
        return Promise.reject(new Error(`Amount must be at least $${wallet?.minAmount}.`));
      }
      if (value > wallet?.maxAmount) {
        return Promise.reject(new Error(`Amount cannot exceed $${wallet?.maxAmount}.`));
      }
    }

    return Promise.resolve();
  };

  const validateAccount = (_, value) => {
    if (!value) return Promise.resolve();

    if (fromAccount === toAccount) {
      return Promise.reject(new Error('From and To accounts must be different.'));
    }

    return Promise.resolve();
  };

  const renderAccounts = (type, value) => (
    <Select
      value={type === 'from' ? fromAccount : toAccount}
      onChange={type === 'from' ? setFromAccount : setToAccount}
      className="w-full !bg-white rounded-xl"
      size="large"
      suffixIcon={<div className="flex items-center justify-center h-full w-full"><DownOutlined className="text-gray-400 bg-[#F5F9FD] p-2.5 rounded-lg" /></div>}
      placeholder={`Select ${type === 'from' ? 'From' : 'To'} Account`}
      popupClassName="custom-select-dropdown"
      dropdownAlign={{ offset: [0, 0] }}
      dropdownStyle={{
        borderRadius: '16px',
        padding: '8px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
        border: 'none',
        marginTop: '-10px'
      }}
    >
      {accounts.map(item => {
        const isWalletAccount = String(item?.login || '').toLowerCase() === 'wallet';
        const accountBalance = isWalletAccount ? wallet?.walletAmount : item?.balance;
        return (
        <Select.Option
          key={item?.login}
          value={item?.login}
          label={<span className="primary font-semibold text-sm">{isWalletAccount ? 'wallet' : item?.login}</span>}
        >
          <div className="flex items-center gap-2 py-1 px-2 rounded-lg">
            {isWalletAccount ? <WalletOutlined size={24} /> : <BankOutlined size={24} />}
            <span className={`font-semibold text-sm`}>
              {isWalletAccount ? 'wallet' : item?.login} {`( $ ${accountBalance || 0 })`}
            </span>
          </div>
        </Select.Option>
        );
      })}
    </Select>
  )

  const infoCards = [
    { title: 'Wallet Account No.', value: wallet?.walletAccountNo, color: "bg-[#E5F1F2]", icon: <Account color="#00727D" /> },
    { title: 'Wallet Balance', value: wallet?.walletAmount, prefix: '$', color: 'bg-[#FFE2E3]', icon: <Balance color="#FF4C51" /> },
    { title: 'Minimum Deposit', value: wallet?.minAmount, prefix: '$', color: 'bg-[#FFF0E1]', icon: <Dollar color="#FF9F43" /> },
    { title: 'Maximum Deposit', value: wallet?.maxAmount, prefix: '$', color: 'bg-[#DDF6E8]', icon: <Dollar color="#28C76F" /> },
  ];

  const balanceCardsData = [
    { id: 1, type: 'Wallet No.', amount: wallet?.walletAccountNo, color: 'bg-[#9810FA]', gradient: 'from-[#FAF5FF] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> },
    { id: 2, type: 'Wallet Balance', amount: wallet?.walletAmount, prefix: '$', color: 'bg-green-500', gradient: 'from-[#F0FDF4] to-[#ffff]', icon: <PlusCircleOutlined size={20} color='#fff' className='text-white' /> },
    { id: 3, type: 'Min Deposit', amount: wallet?.minAmount, prefix: '$', color: 'bg-orange-500', gradient: 'from-[#FFF7ED] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> },
    { id: 4, type: 'Max Deposit', amount: wallet?.maxAmount, prefix: '$', color: 'bg-red-500', gradient: 'from-[#FFEDED] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> }
  ];

  return (
    <div className='mobile-dashboard min-h-screen'>
      <div className="grid grid-cols-4 gap-3 mb-6">
        {balanceCardsData.map((card, index) => (
          <BalanceCard
            key={card.id || index}
            {...card}
            className="text-center px-2 pb-1 py-2"
            innerClassName="flex-col gap-2 !mb-0"
            titleClass='!text-[8px] !leading-none !ml-0'
            amountClass='!text-[10px]'
          />
        ))}
      </div>

      <div className="mb-4">
        <Form form={form} layout='vertical' className='space-y-5' onFinish={handleSubmit}>
          <Form.Item
            name='amount'
            label={<span className="text-lg text-[#314158] font-medium">Amount In USD</span>}
            rules={[{ validator: validateAmount }]}
            className='w-full'
          >
            <Input type='number' placeholder="Amount in USD" size='large' className='py-3' />
          </Form.Item>
          <Form.Item
            name="fromAccount"
            label={<span className="text-lg text-[#314158] font-medium">Select From Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }]}
          >
            {renderAccounts('from', fromAccount)}
          </Form.Item>
          <Form.Item
            name="toAccount"
            dependencies={['fromAccount']}
            label={<span className="text-lg text-[#314158] font-medium">Select To Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }, { validator: validateAccount }]}
          >
            {renderAccounts('to', toAccount)}
          </Form.Item>

          <Form.Item
            name='conditions'
            valuePropName="checked"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
          >
            <div className="flex items-center gap-2">
              <Checkbox className='m-0'>Yes, I agreed to the</Checkbox>
              <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
            </div>
          </Form.Item>

          <LoadableButton
            className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-4 w-full uppercase themeHover duration-500'
            type="submit"
            lable='Submit'
            isLoading={isSubmitLoading}
            loadingLable='Processing...'
          />
        </Form>
      </div>

      <TransferRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
      <OTPModal
        isOpen={isOTPModalOpen}
        setIsOpen={setIsOTPModalOpen}
        title='OTP Verification'
        message='Please enter the OTP to verify internal transfer.'
        width={600}
        value={otp}
        onChange={(val) => setOtp(val)}
        onSubmit={handleOtpSubmit}
        onResend={handleOtpResend}
        isLoading={otpLoading}
        loadingLabel='Verifying...'
        submitLabel='Verify'
        otpDuration={120}
      />
    </div>
  )
}

export default TransferMobile
